import { SelfieSegmentation, type Results } from "@mediapipe/selfie_segmentation";

/**
 * Pipes a MediaStreamTrack through MediaPipe Selfie Segmentation,
 * blurring the background while keeping the speaker sharp.
 *
 * Pipeline:
 *   1. <video> sinks the raw camera frames.
 *   2. SelfieSegmentation.send(image) returns a per-pixel mask.
 *   3. Composite onto an offscreen canvas: blurred background +
 *      hard-cut foreground via globalCompositeOperation.
 *   4. canvas.captureStream() produces a new MediaStreamTrack the
 *      meeting room replaces the outgoing track with.
 *
 * Lifecycle: caller creates the filter, awaits start(), then
 * grabs `outputTrack`. stop() releases the worker + canvas + frame
 * loop. Idempotent — calling stop() twice is a no-op.
 */
export class BackgroundFilter {
    private readonly source: MediaStreamTrack;
    private readonly video: HTMLVideoElement;
    private readonly canvas: HTMLCanvasElement;
    private readonly ctx: CanvasRenderingContext2D;
    private readonly segmenter: SelfieSegmentation;
    private outStream: MediaStream | null = null;
    private rafId: number | null = null;
    private stopped = false;

    constructor(source: MediaStreamTrack) {
        this.source = source;

        // Hidden <video> sink for the raw camera. Width/height come
        // from the track's settings; falling back to 640×480 covers
        // browsers that haven't populated settings yet.
        const settings = source.getSettings();
        const width = settings.width ?? 640;
        const height = settings.height ?? 480;

        this.video = document.createElement("video");
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.autoplay = true;
        this.video.width = width;
        this.video.height = height;

        this.canvas = document.createElement("canvas");
        this.canvas.width = width;
        this.canvas.height = height;
        const ctx = this.canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas 2D context unavailable");
        this.ctx = ctx;

        this.segmenter = new SelfieSegmentation({
            // jsdelivr-hosted assets — same CDN MediaPipe's docs use.
            // Vendoring locally is a cross-cutting concern; sprint 12
            // (PWA) will pull these into the service worker cache.
            locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
        });
        this.segmenter.setOptions({
            // 1 = "landscape" model — better for desktop calls than
            // the default 0 ("general") which targets phone selfies.
            modelSelection: 1
        });
        this.segmenter.onResults((results) => this.onResults(results));
    }

    async start(): Promise<MediaStreamTrack> {
        // Bind the source to the <video> element. We wrap the track
        // in a temporary stream rather than keeping a reference to
        // the caller's stream — the meeting room owns the lifetime
        // of the original camera stream.
        this.video.srcObject = new MediaStream([this.source]);
        await this.video.play();

        // Pre-warm the model — the first send() can take ~500 ms
        // because MediaPipe is downloading WASM + the segmentation
        // graph. Doing it inside the loop pauses the first frame
        // visibly; doing it before play() leaves the canvas blank.
        await this.segmenter.send({ image: this.video });

        // Hand back a track captured from our processed canvas.
        // Match the source frame rate so peers don't see jitter.
        this.outStream = this.canvas.captureStream(30);

        // Drive the segmentation loop on the next frame.
        this.scheduleNextFrame();

        const out = this.outStream.getVideoTracks()[0];
        if (!out) throw new Error("Canvas captureStream returned no video tracks");
        return out;
    }

    stop(): void {
        if (this.stopped) return;
        this.stopped = true;
        if (this.rafId !== null) cancelAnimationFrame(this.rafId);
        this.rafId = null;
        try { this.outStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        try { this.video.pause(); this.video.srcObject = null; } catch { /* ignore */ }
        try { void this.segmenter.close(); } catch { /* ignore */ }
    }

    private scheduleNextFrame(): void {
        if (this.stopped) return;
        this.rafId = requestAnimationFrame(async () => {
            if (this.stopped || this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
                this.scheduleNextFrame();
                return;
            }
            try {
                await this.segmenter.send({ image: this.video });
            } catch {
                // Drop a frame on transient WASM hiccup; the next RAF
                // tick brings the loop back. Repeated failures show
                // up as a frozen tile, not a thrown stack.
            }
            this.scheduleNextFrame();
        });
    }

    private onResults(results: Results): void {
        if (this.stopped) return;
        const { ctx, canvas } = this;

        // Composite recipe (Teams uses the same approach):
        //   1. Draw the segmentation mask. Background = transparent.
        //   2. source-in: keep only the camera pixels overlapping the
        //      mask (the person).
        //   3. destination-over: paint the *blurred* camera underneath
        //      everything we already drew, filling the transparent
        //      background.
        ctx.save();
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(results.segmentationMask, 0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = "source-in";
        ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = "destination-over";
        // 12 px gaussian — strong enough to obscure decor + name
        // tags, light enough to leave window light shapes recognisable
        // (Teams default sits in the same 8–14 px range).
        ctx.filter = "blur(12px)";
        ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);
        ctx.filter = "none";
        ctx.restore();
    }
}
